import React, { useCallback, useMemo } from 'react';
import { Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useModalStore, useToastStore } from '@/store';
import Icon from '@/components/Icon';
import { trackEvent } from '@/constants/utils/analytics';
import { mediumTap } from '@/constants/utils/haptics';

/**
 * Heart to favorite a task template (catalog card). Stops press propagation.
 */
function TaskTemplateFavoriteHeart({ template, hitSlop = 10 }) {
  const { colors } = useTheme();
  const { openModal } = useModalStore(s => s.actions);
  const { addToast } = useToastStore(s => s.actions);
  const token = useAuthStore(s => s.token);
  const hasUser = useAuthStore(s => Boolean(s.user?.id));
  const isGhost = useAuthStore(s => s.user?.flags?.isGhost);
  const favoriteTaskTemplateIds = useAuthStore(s => s.user?.favoriteTaskTemplateIds);
  const toggleFavoriteTaskTemplate = useAuthStore(s => s.toggleFavoriteTaskTemplate);

  const templateId = template?.id != null ? String(template.id) : '';
  const isFavorited = !!(templateId && (favoriteTaskTemplateIds || []).map(String).includes(templateId));

  const styles = useMemo(() => StyleSheet.create({
    hit: {
      padding: 6,
      borderRadius: 8,
      justifyContent: 'center',
      alignItems: 'center',
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    },
  }), []);

  const requireAuth = useCallback(() => {
    if (token && !isGhost) return false;
    trackEvent('tasks', 'task_templates_favorite_auth_required', templateId);
    openModal(isGhost ? 'GhostSignupModal' : 'SignupModal');
    return true;
  }, [token, isGhost, openModal, templateId]);

  const onPress = useCallback(async (e) => {
    e?.stopPropagation?.();
    if (!templateId) return;
    if (requireAuth()) return;
    try {
      const nowFavorited = await toggleFavoriteTaskTemplate(templateId);
      mediumTap();
      trackEvent('tasks', nowFavorited ? 'task_templates_favorite' : 'task_templates_unfavorite', templateId);
    } catch (_) {
      addToast('Could not update favorite', 'error');
    }
  }, [templateId, requireAuth, toggleFavoriteTaskTemplate, addToast]);

  // Favoriting is account-bound; logged-out visitors don't see the control.
  if (!templateId || !hasUser) return null;

  return (
    <Pressable
      onPress={onPress}
      hitSlop={hitSlop}
      style={({ hovered, pressed }) => [
        styles.hit,
        (hovered || pressed) && { backgroundColor: colors.solid_color_tertiary },
      ]}
      accessibilityRole="button"
      accessibilityLabel={isFavorited ? 'Remove template from favorites' : 'Save template to favorites'}
      accessibilityState={{ selected: isFavorited }}
    >
      <Icon
        name="heart"
        size={15}
        solid={isFavorited}
        color={isFavorited ? '#ef4444' : colors.text_color_tertiary}
      />
    </Pressable>
  );
}

export default React.memo(TaskTemplateFavoriteHeart);
